import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Glassmorphism styles
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.08)',
  borderRadius: '20px',
};

const cardStyle = {
  ...glassStyle,
  p: 4,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.12)',
  }
};

interface ProviderStatus {
  has_key: boolean;
  has_custom_key: boolean;
}

interface AISettingsData {
  default_provider: string;
  default_model: string;
  providers: {
    openai: ProviderStatus;
    anthropic: ProviderStatus;
    gemini: ProviderStatus;
  };
  updated_at: string;
}

// Modelos atualizados
const MODELS_BY_PROVIDER: Record<string, { value: string; label: string; description: string }[]> = {
  openai: [
    { value: 'gpt-5.2', label: 'GPT-5.2 (Recomendado)', description: 'Mais avançado e capaz' },
    { value: 'gpt-5.2-mini', label: 'GPT-5.2 Mini', description: 'Rápido e econômico' },
    { value: 'gpt-4.1', label: 'GPT-4.1', description: 'Alta qualidade' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', description: 'Equilibrado' },
  ],
  anthropic: [
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5', description: 'Máxima qualidade' },
    { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (Recomendado)', description: 'Equilibrado' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet', description: 'Versão anterior' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku', description: 'Mais rápido' },
  ],
  gemini: [
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Recomendado)', description: 'Mais recente' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro', description: 'Alta qualidade' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash', description: 'Mais rápido' },
  ],
};

// Provider Logos as SVG components
const OpenAILogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#10a37f">
    <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4066-.6898zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
  </svg>
);

const AnthropicLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#cc785c">
    <path d="M13.827 3.52h3.603L24 20.48h-3.603L13.827 3.52zm-7.257 0h3.603l6.57 16.96h-3.603L6.57 3.52zM0 20.48h3.603L7.173 10.2l-1.8-4.65L0 20.48z"/>
  </svg>
);

const GeminiLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path d="M12 24C12 24 12 24 12 24C5.37 24 0 18.63 0 12C0 12 0 12 0 12C6.63 12 12 6.63 12 0C12 0 12 0 12 0C12 6.63 17.37 12 24 12C24 12 24 12 24 12C17.37 12 12 17.37 12 24Z" fill="url(#gemini-gradient)"/>
    <defs>
      <linearGradient id="gemini-gradient" x1="0" y1="12" x2="24" y2="12">
        <stop stopColor="#4285f4"/>
        <stop offset="0.5" stopColor="#9b72cb"/>
        <stop offset="1" stopColor="#d96570"/>
      </linearGradient>
    </defs>
  </svg>
);

const PROVIDERS = [
  { id: 'openai', name: 'OpenAI', Logo: OpenAILogo, color: '#10a37f', bgColor: 'rgba(16, 163, 127, 0.1)' },
  { id: 'anthropic', name: 'Anthropic', Logo: AnthropicLogo, color: '#cc785c', bgColor: 'rgba(204, 120, 92, 0.1)' },
  { id: 'gemini', name: 'Google Gemini', Logo: GeminiLogo, color: '#4285f4', bgColor: 'rgba(66, 133, 244, 0.1)' },
];

const AISettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AISettingsData | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [model, setModel] = useState('gpt-5.2');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Carregar configurações do backend
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ai/settings/get_settings/');
      setSettings(response.data);
      setSelectedProvider(response.data.default_provider || 'openai');
      setModel(response.data.default_model || 'gpt-5.2');
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  // Salvar API Key
  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      setTestResult({ success: false, message: 'Por favor, insira uma API key' });
      return;
    }

    setSaving(true);
    setTestResult(null);

    try {
      await api.post('/ai/settings/configure_api_key/', {
        provider: selectedProvider,
        api_key: apiKey
      });

      setSaveSuccess(true);
      setApiKey('');
      await loadSettings();
      setTimeout(() => setSaveSuccess(false), 3000);
      setTestResult({ success: true, message: 'API key salva com sucesso!' });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Erro ao salvar API key'
      });
    } finally {
      setSaving(false);
    }
  };

  // Salvar configurações padrão
  const handleSaveDefaults = async () => {
    setSaving(true);
    try {
      await api.post('/ai/settings/update_defaults/', {
        default_provider: selectedProvider,
        default_model: model
      });
      setSaveSuccess(true);
      await loadSettings();
      setTimeout(() => setSaveSuccess(false), 3000);
      setTestResult({ success: true, message: 'Configurações padrão salvas!' });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Erro ao salvar configurações'
      });
    } finally {
      setSaving(false);
    }
  };

  // Testar conexão
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await api.post('/ai/settings/test_connection/', {
        provider: selectedProvider
      });

      if (response.data.success) {
        setTestResult({
          success: true,
          message: response.data.message || 'Conexão bem-sucedida!'
        });
      } else {
        setTestResult({
          success: false,
          message: response.data.error || 'Erro na conexão'
        });
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Erro ao testar conexão'
      });
    } finally {
      setTesting(false);
    }
  };

  // Remover API Key
  const handleClearApiKey = async (provider: string) => {
    if (!confirm(`Tem certeza que deseja remover a API key do ${provider}?`)) return;

    try {
      await api.post('/ai/settings/clear_api_key/', { provider });
      await loadSettings();
      setTestResult({
        success: true,
        message: `API key do ${provider} removida`
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Erro ao remover API key'
      });
    }
  };

  const handleProviderChange = (providerId: string) => {
    setSelectedProvider(providerId);
    const models = MODELS_BY_PROVIDER[providerId] || [];
    if (models.length > 0 && !models.find(m => m.value === model)) {
      setModel(models[0].value);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  const currentModels = MODELS_BY_PROVIDER[selectedProvider] || [];
  const currentProviderStatus = settings?.providers[selectedProvider as keyof typeof settings.providers];
  const currentProviderInfo = PROVIDERS.find(p => p.id === selectedProvider);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 50%, #f0f9ff 100%)',
      p: 4,
    }}>
      {/* Decorative elements */}
      <Box sx={{
        position: 'fixed',
        top: '10%',
        right: '10%',
        width: 300,
        height: 300,
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0,
      }} />
      <Box sx={{
        position: 'fixed',
        bottom: '20%',
        left: '5%',
        width: 250,
        height: 250,
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <Box sx={{ maxWidth: 900, mx: 'auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/settings')}
              sx={{
                background: 'rgba(255,255,255,0.8)',
                '&:hover': { background: 'rgba(255,255,255,0.95)' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToyIcon sx={{ fontSize: 32, color: '#6366f1' }} />
                Configurações de IA
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 0.5 }}>
                Configure as API keys e preferências dos provedores de IA
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Recarregar configurações">
            <IconButton onClick={loadSettings} sx={{
              background: 'rgba(255,255,255,0.8)',
              '&:hover': { background: 'rgba(255,255,255,0.95)' }
            }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Alert de resultado */}
        {testResult && (
          <Alert
            severity={testResult.success ? 'success' : 'error'}
            sx={{ mb: 3, ...glassStyle, borderRadius: '12px' }}
            onClose={() => setTestResult(null)}
          >
            {testResult.message}
          </Alert>
        )}

        {/* Status dos Providers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 4 }}>
          {PROVIDERS.map((provider) => {
            const status = settings?.providers[provider.id as keyof typeof settings.providers];
            const isSelected = selectedProvider === provider.id;
            return (
              <Card
                key={provider.id}
                onClick={() => handleProviderChange(provider.id)}
                sx={{
                  ...glassStyle,
                  p: 3,
                  cursor: 'pointer',
                  border: isSelected ? `2px solid ${provider.color}` : '1px solid rgba(255, 255, 255, 0.3)',
                  background: isSelected ? provider.bgColor : 'rgba(255, 255, 255, 0.7)',
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.15)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <provider.Logo />
                  {status?.has_key ? (
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                      label="Configurado"
                      size="small"
                      sx={{
                        background: 'rgba(34, 197, 94, 0.15)',
                        color: '#16a34a',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        '& .MuiChip-icon': { color: '#16a34a' }
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                      label="Não configurado"
                      size="small"
                      sx={{
                        background: 'rgba(148, 163, 184, 0.15)',
                        color: '#64748b',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                  {provider.name}
                </Typography>
                {status?.has_custom_key && (
                  <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mt: 0.5 }}>
                    ✓ API key personalizada salva
                  </Typography>
                )}
              </Card>
            );
          })}
        </Box>

        {/* Card Principal de Configuração */}
        <Card sx={{ ...cardStyle, mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyIcon sx={{ color: currentProviderInfo?.color || '#6366f1' }} />
            Configurar {currentProviderInfo?.name}
            {currentProviderStatus?.has_key && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label="API Key Salva"
                size="small"
                sx={{
                  ml: 2,
                  background: 'rgba(34, 197, 94, 0.15)',
                  color: '#16a34a',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: '#16a34a' }
                }}
              />
            )}
          </Typography>

          {/* Indicador visual de API Key configurada */}
          {currentProviderStatus?.has_key && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                '& .MuiAlert-icon': { color: '#16a34a' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography sx={{ fontWeight: 500 }}>
                  API Key do {currentProviderInfo?.name} está configurada e ativa
                </Typography>
                {currentProviderStatus?.has_custom_key && (
                  <Tooltip title="Remover API Key">
                    <IconButton
                      size="small"
                      onClick={() => handleClearApiKey(selectedProvider)}
                      sx={{ color: '#dc2626', ml: 2 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Alert>
          )}

          {/* Input de API Key */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={currentProviderStatus?.has_key ? 'Digite nova API key para substituir...' : 'Cole sua API key aqui...'}
              label={`API Key - ${currentProviderInfo?.name}`}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowKey(!showKey)} edge="end">
                      {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: '#fff',
                  borderRadius: '12px',
                }
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                Sua chave é criptografada e armazenada com segurança
              </Typography>
              <Typography
                component="a"
                href={
                  selectedProvider === 'openai' ? 'https://platform.openai.com/api-keys' :
                  selectedProvider === 'anthropic' ? 'https://console.anthropic.com/settings/keys' :
                  'https://makersuite.google.com/app/apikey'
                }
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#6366f1',
                  fontSize: '0.75rem',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Obter API key →
              </Typography>
            </Box>
          </Box>

          {/* Botões de ação */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SaveIcon />}
              onClick={handleSaveApiKey}
              disabled={!apiKey || saving}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${currentProviderInfo?.color || '#6366f1'} 0%, #8b5cf6 100%)`,
                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
                },
                '&:disabled': {
                  background: '#e2e8f0',
                }
              }}
            >
              {saving ? 'Salvando...' : 'Salvar API Key'}
            </Button>
            <Button
              variant="outlined"
              startIcon={testing ? <CircularProgress size={20} /> : <ScienceIcon />}
              onClick={handleTest}
              disabled={!currentProviderStatus?.has_key || testing}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: currentProviderInfo?.color || '#6366f1',
                color: currentProviderInfo?.color || '#6366f1',
                '&:hover': {
                  borderColor: currentProviderInfo?.color || '#6366f1',
                  background: currentProviderInfo?.bgColor || 'rgba(99, 102, 241, 0.1)',
                }
              }}
            >
              {testing ? 'Testando...' : 'Testar Conexão'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Seleção de Modelo Padrão */}
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
            Modelo Padrão
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Modelo</InputLabel>
            <Select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              label="Modelo"
              sx={{
                background: '#fff',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '12px',
                }
              }}
            >
              {currentModels.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{m.description}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveDefaults}
            disabled={saving}
            sx={{
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#e2e8f0',
              color: '#64748b',
              background: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: '#6366f1',
                color: '#6366f1',
                background: 'rgba(99, 102, 241, 0.05)',
              }
            }}
          >
            Salvar como Padrão
          </Button>
        </Card>

        {/* Informações sobre funcionalidades */}
        <Card sx={{ ...cardStyle, background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
            Funcionalidades de IA Disponíveis
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {[
              'Agentes de IA automatizados',
              'Transcrição de áudio (Whisper)',
              'Análise de imagens (Vision)',
              'Processamento de documentos',
              'Handoff para atendimento humano',
              'Integração com WhatsApp',
            ].map((feature, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                <Typography sx={{ color: '#374151', fontSize: '0.9rem' }}>{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Card>

        {/* Estimativa de custos */}
        <Card sx={{ ...cardStyle, mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
            Estimativa de Custos
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            <Box sx={{ p: 2, background: 'rgba(16, 163, 127, 0.1)', borderRadius: '12px' }}>
              <Typography sx={{ fontWeight: 600, color: '#10a37f', mb: 1 }}>OpenAI</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>GPT-4o: ~$0.01/interação</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>Whisper: ~$0.006/min</Typography>
            </Box>
            <Box sx={{ p: 2, background: 'rgba(204, 120, 92, 0.1)', borderRadius: '12px' }}>
              <Typography sx={{ fontWeight: 600, color: '#cc785c', mb: 1 }}>Anthropic</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>Sonnet 4: ~$0.015/interação</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>Opus: ~$0.075/interação</Typography>
            </Box>
            <Box sx={{ p: 2, background: 'rgba(66, 133, 244, 0.1)', borderRadius: '12px' }}>
              <Typography sx={{ fontWeight: 600, color: '#4285f4', mb: 1 }}>Gemini</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>Flash: ~$0.001/interação</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>Pro: ~$0.007/interação</Typography>
            </Box>
          </Box>
          <Typography sx={{ mt: 2, color: '#64748b', fontSize: '0.8rem', textAlign: 'center' }}>
            Volume médio mensal: 1000-2000 interações = $10-50/mês
          </Typography>
        </Card>
      </Box>
    </Box>
  );
};

export default AISettings;
